import { ExternalProviderLinkModel } from "@insite/client-framework/Services/IdentityService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import signInExternalProvider from "@insite/client-framework/Store/Context/Handlers/SignInExternalProvider";
import loadExternalProviders from "@insite/client-framework/Store/Pages/SignIn/Handlers/LoadExternalProviders";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    externalProviders: state.pages.signIn.externalProviders,
});

const mapDispatchToProps = {
    loadExternalProviders,
    signInExternalProvider,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SignInExternalProvidersStyles {
    divider?: InjectableCss;
    titleText?: TypographyPresentationProps;
    providerButton?: ButtonPresentationProps;
    ssoLoginButton?: LinkPresentationProps;
}

export const signInExternalProvidersStyles: SignInExternalProvidersStyles = {
    divider: {
        css: css`
            margin: 30px 0 20px 0;
        `,
    },
    titleText: {
        weight: "bold",
    },
    ssoLoginButton: {
        typographyProps: {
            weight: 500,
            size: 15,
            css: css`
                padding: 0px !important;
            `,
        },
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px !important;
                        margin: auto;
                        width: 100%;
                    `,
                    css`
                        padding: 0px !important;
                        margin: auto;
                        width: 100%;
                    `,
                    css`
                        padding: 0px !important;
                        margin: auto;
                        width: 100%;
                    `,
                    css`
                        padding: 0px !important;
                        margin: auto;
                        width: 100%;
                    `,
                    css`
                        padding: 0px !important;
                        margin: auto;
                        width: 100%;
                    `,
                ])}
        `,
    },
};

const styles = signInExternalProvidersStyles;

const SignInExternalProviders = ({
    externalProviders,
    loadExternalProviders,
    signInExternalProvider,
    fields,
}: Props) => {
    useEffect(() => {
        if (!externalProviders) {
            loadExternalProviders();
        }
    }, [externalProviders]);

    const providerButtonClickHandler = (externalProvider: ExternalProviderLinkModel) => {
        signInExternalProvider({ externalProvider });
    };

    if (!externalProviders || externalProviders.length === 0) {
        return null;
    }

    const providerButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <>
            {externalProviders.map(externalProvider => (
                <Link
                    {...styles.ssoLoginButton}
                    key={externalProvider.caption}
                    type="button"
                    onClick={() => providerButtonClickHandler(externalProvider)}
                    role="link"
                >
                    {externalProvider.caption}
                </Link>
            ))}
        </>
    );
};

const Divider = styled.hr`
    ${styles.divider?.css}
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SignInExternalProviders),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        displayName: "External Providers",
        fieldDefinitions: [
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
